import "reflect-metadata";
import { z } from "zod";

/**
 * Metadata interface for API method decorators
 */
export interface MethodMetadata {
    /** Description of the API method */
    description: string;
    /** HTTP verb (GET, POST, PUT, DELETE, etc.) */
    httpVerb: "GET" | "POST" | "PUT" | "DELETE";
    /** API endpoint path (e.g., "/quotes", "/payments/session") */
    endpoint: string;
    /** Zod schema for request input validation */
    inputSchema?: z.ZodSchema;
    /** Zod schema for response output validation */
    outputSchema?: z.ZodSchema;
}

/**
 * Symbol key for storing API method metadata
 */
export const API_METHOD_METADATA_KEY = Symbol("api-method-metadata");

// A global registry that maps endpoint strings (with HTTP verb) to MethodMetadata
export const methodMetadataRegistry = new Map<string, MethodMetadata>();

/**
 * @MethodMetadata decorator factory
 *   - Attaches `config` to the prototype under MetadataKey.MethodMetadata
 *   - Injects a static toJSON(methodName) on the constructor
 */
export const MethodMetadata = (config: MethodMetadata) => {
    return (target: object, propertyKey: string | symbol) => {
        // 1) Store metadata on the prototype for this method
        Reflect.defineMetadata(API_METHOD_METADATA_KEY, config, target, propertyKey);

        // 2) Store metadata in the global registry
        const httpVerb = config.httpVerb;
        const endpoint = config.endpoint;
        const key = `${httpVerb.toLowerCase()} ${endpoint}`;
        methodMetadataRegistry.set(key, config);

        // 3) Inject static toJSON(...) on the class constructor if not already defined
        const ctor = (target as any).constructor as MethodMetadataContainer;
        if (typeof ctor.toJSON !== "function") {
            Object.defineProperty(ctor, "toJSON", {
                value: (methodName: string): MethodMetadata | undefined => {
                    return Reflect.getMetadata(API_METHOD_METADATA_KEY, ctor.prototype, methodName);
                },
                writable: false,
                configurable: false,
            });
        }
    };
};

/**
 * By declaring this interface, TypeScript knows
 * that any class decorated with @MethodMetadata has a static toJSON method.
 */
export interface MethodMetadataContainer {
    new (...args: any[]): unknown;
    toJSON(methodName: string): MethodMetadata | undefined;
}
